// src/components/Footer.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { useTranslation } from 'react-i18next'; // Import useTranslation

const Footer: React.FC = () => {
  const { t } = useTranslation(); // Initialize useTranslation hook

  return (
    <footer className="bg-[#001F54] text-white py-12 -mt-[230px]">
      <div className="max-w-7xl mx-auto px-4 md:px-10 ">

        {/* Bottom Section: Standard Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mt-48">
          {/* Column 1: Download App */}
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">{t('footer_download_app_heading')}</h3> {/* Translated */}
            <div className="flex flex-col space-y-4">
               <motion.a
                            href="https://play.google.com/store/apps/details?id=com.CashOnCalls"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center bg-black text-white rounded-lg px-4 py-2 hover:bg-gray-800 transition-all duration-300"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaGooglePlay className="text-2xl mr-3 text-green-400" />
                            <div className="text-left text-sm leading-tight">
                                <div className="text-xs">{t('get_it_on_label')}</div> {/* Reused from Hero.tsx */}
                                <div className="font-semibold text-base">{t('google_play_label')}</div> {/* Reused from Hero.tsx */}
                            </div>
                        </motion.a>

                        <motion.a
                            href="https://apps.apple.com/us/app/COC-spam-blocker/id525881958"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center bg-black text-white rounded-lg px-4 py-2 hover:bg-gray-800 transition-all duration-300"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaApple className="text-2xl mr-3" />
                            <div className="text-left text-sm leading-tight">
                                <div className="text-xs">{t('download_on_label')}</div> {/* Reused from Hero.tsx */}
                                <div className="font-semibold text-base">{t('app_store_label')}</div> {/* Reused from Hero.tsx */}
                            </div>
                        </motion.a>
            </div>
          </div>

          {/* Column 2: COC for */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer_coc_for_heading')}</h3> {/* Translated */}
            <ul className="space-y-2">
              <li><a href="#" className="hover:underline">{t('footer_coc_android')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_coc_ios')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_coc_web')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_coc_business')}</a></li> {/* Translated */}
            </ul>
          </div>

          {/* Column 3: Features */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer_features_heading')}</h3> {/* Translated */}
            <ul className="space-y-2">
              <li><a href="#" className="hover:underline">{t('footer_feature_spam_blocking')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_feature_caller_id')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_feature_spam_detection')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_feature_call_recording')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_feature_privacy')}</a></li> {/* Translated */}
            </ul>
          </div>

          {/* Column 4: Community & Support */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer_community_support_heading')}</h3> {/* Translated */}
            <ul className="space-y-2">
              <li><a href="#" className="hover:underline">{t('footer_customer_support_community')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_request_blocklist_exception')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_coc_101')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_coc_101_alt')}</a></li> {/* Translated */} {/* Changed key to avoid duplicate */}
            </ul>
          </div>

          {/* Column 5: Company */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer_company_heading')}</h3> {/* Translated */}
            <ul className="space-y-2">
              <li><a href="#" className="hover:underline">{t('footer_customer_support_company')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_contact_us')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_careers')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_about_us')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_manage_preferences')}</a></li> {/* Translated */}
              <li><a href="#" className="hover:underline">{t('footer_law_enforcement')}</a></li> {/* Translated */}
            </ul>
          </div>
        </div>

        {/* Bottom-most Section: Copyright and Legal Links */}
        <div className="mt-12 border-t border-gray-700 pt-8 text-center text-sm">
          <p className="mb-4">{t('footer_copyright_text')}</p> {/* Translated */}
          <div className="flex justify-center space-x-6">
            <a href="#" className="hover:underline">{t('footer_privacy_policy')}</a> {/* Translated */}
            <a href="#" className="hover:underline">{t('footer_terms_of_service')}</a> {/* Translated */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;